import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../../services/api';

const GradientHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, #F3F2F1 0%, #1F648B 100%)',
  boxShadow: '0 4px 16px rgba(31,100,139,0.15)',
  borderRadius: '0 0 24px 24px',
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Segoe UI, Arial, sans-serif',
  fontWeight: 700,
  fontSize: 28,
  color: 'white',
  textShadow: '0 2px 8px #1976d2, 0 1px 0 #fff',
  letterSpacing: 1,
  background: 'linear-gradient(90deg, #F3F2F1 0%, #1F648B 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const ListPanel = styled(Paper)(({ theme, bgcolor }) => ({
  width: 300,
  height: 320,
  background: bgcolor,
  color: 'white',
  borderRadius: 16,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px 0',
  overflowY: 'auto',
  fontFamily: 'Segoe UI, Arial, sans-serif',
  fontSize: 18,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: 8,
    background: '#1F497D',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#4C535C',
    borderRadius: 8,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: '#FDFDFD',
  color: '#1F648B',
  fontWeight: 600,
  fontSize: 16,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(31,100,139,0.10)',
  padding: '8px 32px',
  marginTop: 8,
  marginBottom: 8,
  '&:hover': {
    background: '#E0F7FA',
    color: '#1976d2',
  },
}));


const ShopKarigarTransaction = () => {
  const [orders, setOrders] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [karats, setKarats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState(null);
  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [selectedKaratIndex, setSelectedKaratIndex] = useState(null);
  const [selectedKarigarIndex, setSelectedKarigarIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const ordersRes = await api.get('/api/shop/orders');
        const karigarsRes = await api.get('/api/karigar');
        const itemsRes = await api.get('/api/items');
        setOrders(ordersRes.data || []);
        setKarigars(karigarsRes.data || []);
        // Extract unique karats from items
        const uniqueKarats = Array.from(new Set((itemsRes.data || []).map(item => item.karat).filter(Boolean)));
        setKarats(uniqueKarats);
      } catch (err) {
        setOrders([]);
        setKarigars([]);
        setKarats([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
  <Box sx={{ width: '100vw', maxWidth: '100vw', minHeight: '100vh', bgcolor: '#8EBBF5', fontFamily: 'Segoe UI, Arial, sans-serif', overflowX: 'hidden', overflowY: 'auto', m: 0, p: 0, margin: 0, padding: 0, boxSizing: 'border-box', position: 'absolute', left: 0, top: 0, pt: '80px' }}>
      <GradientHeader>
        <TitleText>
          Karigar Transaction Search Criterion Form
        </TitleText>
      </GradientHeader>
      {/* First row: Voucher No, Karigar Name, Voucher Date, Karat */}
  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 8, justifyContent: 'center', mt: 0, mb: 0 }}>
        <ListPanel bgcolor="#4C535C" style={{ width: '18%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Voucher No</Typography>
          {loading ? <CircularProgress color="inherit" /> : (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {orders && orders.length > 0 ? orders.map((order, idx) => (
                <Box
                  key={order._id}
                  sx={{ mb: 1, p: 1, bgcolor: selectedOrderIndex === idx ? '#1976d2' : '#1F497D', borderRadius: 2, cursor: 'pointer', fontWeight: selectedOrderIndex === idx ? 700 : 400 }}
                  onClick={() => setSelectedOrderIndex(idx)}
                >
                  {order.billNumber || '-'} {order.billName || '-'}
                </Box>
              )) : <Typography>No orders found.</Typography>}
            </Box>
          )}
        </ListPanel>
        <ListPanel bgcolor="#1F497D" style={{ width: '18%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Karigar Name</Typography>
          {loading ? <CircularProgress color="inherit" /> : (
            <Box sx={{ width: '100%', maxHeight: 250, overflowY: 'auto' }}>
              {karigars && karigars.length > 0 ? karigars.map((karigar, idx) => (
                <Box
                  key={karigar._id}
                  sx={{ mb: 1, p: 1, bgcolor: selectedKarigarIndex === idx ? '#1976d2' : '#4C535C', borderRadius: 2, cursor: 'pointer', fontWeight: selectedKarigarIndex === idx ? 700 : 400 }}
                  onClick={() => setSelectedKarigarIndex(idx)}
                >
                  {karigar.name}
                </Box>
              )) : <Typography>No karigars found.</Typography>}
            </Box>
          )}
        </ListPanel>
        <ListPanel bgcolor="#4C535C" style={{ width: '18%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Voucher Date</Typography>
          {loading ? <CircularProgress color="inherit" /> : (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {orders && orders.length > 0 ? orders.map((order, idx) => (
                <Box
                  key={order._id}
                  sx={{ mb: 1, p: 1, bgcolor: selectedDateIndex === idx ? '#1976d2' : '#1F497D', borderRadius: 2, cursor: 'pointer', fontWeight: selectedDateIndex === idx ? 700 : 400 }}
                  onClick={() => setSelectedDateIndex(idx)}
                >
                  {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '-'}
                </Box>
              )) : <Typography>No orders found.</Typography>}
            </Box>
          )}
        </ListPanel>
        <ListPanel bgcolor="#1F497D" style={{ width: '18%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Karat</Typography>
          {loading ? <CircularProgress color="inherit" /> : (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {karats.length > 0 ? karats.map((karat, idx) => (
                <Box
                  key={karat}
                  sx={{ mb: 1, p: 1, bgcolor: selectedKaratIndex === idx ? '#1976d2' : '#4C535C', borderRadius: 2, cursor: 'pointer', fontWeight: selectedKaratIndex === idx ? 700 : 400 }}
                  onClick={() => setSelectedKaratIndex(idx)}
                >
                  {karat}
                </Box>
              )) : <Typography>No karats found.</Typography>}
            </Box>
          )}
        </ListPanel>
      </Box>
      {/* Second row: Search Forms, Screen Report, Print Report */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 8, justifyContent: 'center', mt: 4 }}>
        <ListPanel bgcolor="#1F497D" style={{ width: '22%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Search Forms</Typography>
          {[
            {
              label: 'Open Work Receipt List',
              href: `/shop/work-receipt?karigar=${selectedKarigarIndex !== null ? karigars[selectedKarigarIndex]?.abbreviation : ''}&karat=${selectedKaratIndex !== null ? karats[selectedKaratIndex] : ''}`,
              disabled: false
            },
            {
              label: 'Open Metal Issue List',
              href: `/shop/metal-issue-list?karigar=${selectedKarigarIndex !== null ? karigars[selectedKarigarIndex]?.abbreviation : ''}&karat=${selectedKaratIndex !== null ? karats[selectedKaratIndex] : ''}`,
              disabled: false
            },
            {
              label: 'Karigar Overview',
              href: `/shop/shop-karigar-overview?karigar=${selectedKarigarIndex !== null ? karigars[selectedKarigarIndex]?.abbreviation : ''}&karat=${selectedKaratIndex !== null ? karats[selectedKaratIndex] : ''}`,
              disabled: false
            },
            { label: 'Open Work Issue List', href: '', disabled: true },
            { label: 'Open Loss Issue List', href: '', disabled: true },
          ].map((option, idx) => (
            <Box
              key={option.label}
              sx={{ mb: 1, p: 1, bgcolor: option.disabled ? '#4C535C' : '#1976d2', borderRadius: 2, cursor: option.disabled ? 'not-allowed' : 'pointer', fontWeight: option.disabled ? 400 : 700, color: 'white', opacity: option.disabled ? 0.6 : 1 }}
              component={option.disabled ? 'div' : 'a'}
              href={option.href}
              target={option.disabled ? undefined : '_self'}
            >
              {option.label}
            </Box>
          ))}
        </ListPanel>
        <ListPanel bgcolor="#4C535C" style={{ width: '22%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Screen Report</Typography>
          {/* Add your Screen Report options here */}
        </ListPanel>
        <ListPanel bgcolor="#1F497D" style={{ width: '22%', minWidth: 160, maxWidth: 300 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Print Report</Typography>
          {/* Add your Print Report options here */}
        </ListPanel>
      </Box>
    </Box>
  );
};

export default ShopKarigarTransaction;
